import { MagicConfigMixin, debounce } from '@gig/magic-common';
import { __extends, __assign, __awaiter, __generator, __spreadArray, __decorate, __metadata } from 'tslib';
import { Prop, Emit, Watch, Component, Mixins } from 'vue-property-decorator';
import { ValidationDisplay } from '../../types/form-builder.interface.js';

var AsyncDebounceInMs = 200;
var FormBuilderPartial = /** @class */ (function (_super) {
    __extends(FormBuilderPartial, _super);
    function FormBuilderPartial() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.debouncedValidators = {};
        return _this;
    }
    FormBuilderPartial.prototype.fieldValue = function (field) {
        var _a, _b;
        // let's check first if there's a value in the  form model
        var fieldId = field === null || field === void 0 ? void 0 : field.id;
        if (fieldId &&
            this.formData[fieldId] &&
            ((_a = this.formData[fieldId]) === null || _a === void 0 ? void 0 : _a.value) !== null) {
            return this.formData[fieldId].value;
        }
        // ... if not, let's see if we must set a config defined value
        var strictValue = (_b = field === null || field === void 0 ? void 0 : field.properties) === null || _b === void 0 ? void 0 : _b.value;
        if (strictValue) {
            return strictValue;
        }
        // ... finally, if the field has dependency, let's get the value from field dependency
        if (field.dependency) {
            return this.formData[field.dependency].value;
        }
        return;
    };
    FormBuilderPartial.prototype.vProp = function (item, prop) {
        var _a;
        return (_a = item.v) === null || _a === void 0 ? void 0 : _a[prop];
    };
    FormBuilderPartial.prototype.isVisible = function (field) {
        var fieldId = field.validationBase || field.id;
        if (!this.formData[fieldId]) {
            return true;
        }
        return !this.formData[fieldId].hidden;
    };
    FormBuilderPartial.prototype.getItemPrimitiveProperties = function (item) {
        var properties = this.getItemProperties(item);
        return Object.keys(properties).reduce(function (acc, key) {
            var _a;
            if (typeof properties[key] === 'object') {
                return acc;
            }
            return __assign(__assign({}, acc), (_a = {}, _a[key] = properties[key], _a));
        }, {});
    };
    FormBuilderPartial.prototype.getItemProperties = function (item) {
        var _a;
        var properties = __assign({}, item.properties);
        var fieldId = item.validationBase || item.id;
        if ((_a = this.formData[fieldId]) === null || _a === void 0 ? void 0 : _a.disabled) {
            properties.disabled = true;
        }
        if (!item.dynamicProperties) {
            return properties;
        }
        var calculatedDynamicProperties = this.calculateDynamicProperties(item.dynamicProperties);
        return __assign(__assign({}, properties), calculatedDynamicProperties);
    };
    /**
     * Execute dynamic property compute function and returns an object containing
     * computed value for each dynamic property.
     */
    FormBuilderPartial.prototype.calculateDynamicProperties = function (dynamicProperties) {
        var _this = this;
        return Object.entries(dynamicProperties).reduce(function (acc, _a) {
            var _b;
            var name = _a[0], fx = _a[1];
            return __assign(__assign({}, acc), (_b = {}, _b[name] = fx(_this.formData), _b));
        }, {});
    };
    FormBuilderPartial.prototype.hasError = function (field) {
        var _a;
        if (this.hasFieldProperty(field, 'has-error')) {
            return this.getFieldProperty(field, 'has-error');
        }
        var fieldId = field.validationBase || field.id;
        if (!this.formData[fieldId] ||
            !this.formData[fieldId].touched ||
            this.formData[fieldId].isValidationPending) {
            return;
        }
        return !((_a = this.formData[fieldId]) === null || _a === void 0 ? void 0 : _a.isValid);
    };
    FormBuilderPartial.prototype.errorMessage = function (field) {
        var _a;
        if (!this.shouldShowValidationMessages(field)) {
            return undefined;
        }
        // in case the error message is added through configuration, we override the logic
        var propertiesMessage = (_a = field === null || field === void 0 ? void 0 : field.properties) === null || _a === void 0 ? void 0 : _a.message;
        if (propertiesMessage) {
            return propertiesMessage;
        }
        var fieldId = field.validationBase || field.id;
        if (!this.formData[fieldId] ||
            this.formData[fieldId].isValidationPending) {
            return '';
        }
        return this.formData[fieldId].isValid === false
            ? this.computeErrorMessage(this.formData[fieldId])
            : undefined;
    };
    FormBuilderPartial.prototype.computeErrorMessage = function (field) {
        return typeof field.error === 'function'
            ? field.error(field.value)
            : field.error;
    };
    FormBuilderPartial.prototype.hasSuccess = function (field) {
        var _a, _b, _c;
        if (this.hasFieldProperty(field, 'has-success')) {
            return this.getFieldProperty(field, 'has-success');
        }
        var fieldId = field.validationBase || field.id;
        if (field.isOptional &&
            (((_a = this.formData[fieldId]) === null || _a === void 0 ? void 0 : _a.value) === null ||
                ((_b = this.formData[fieldId]) === null || _b === void 0 ? void 0 : _b.value) === '')) {
            return;
        }
        if (!this.formData[fieldId] ||
            this.formData[fieldId].isValidationPending) {
            return;
        }
        return !!((_c = this.formData[fieldId]) === null || _c === void 0 ? void 0 : _c.isValid);
    };
    FormBuilderPartial.prototype.hasWarning = function (field) {
        var _a;
        if (this.hasFieldProperty(field, 'has-warning')) {
            return this.getFieldProperty(field, 'has-warning');
        }
        var fieldId = field.validationBase || field.id;
        if (!this.formData[fieldId] ||
            !this.formData[fieldId].isValidationPending) {
            return;
        }
        return !!((_a = this.formData[fieldId]) === null || _a === void 0 ? void 0 : _a.isValidationPending);
    };
    FormBuilderPartial.prototype.hasFieldProperty = function (field, property) {
        var _a, _b;
        return (((_a = field.properties) === null || _a === void 0 ? void 0 : _a[property]) !== undefined ||
            ((_b = field.dynamicProperties) === null || _b === void 0 ? void 0 : _b[property]) !== undefined);
    };
    FormBuilderPartial.prototype.getFieldProperty = function (field, property) {
        var _a, _b;
        if ((_a = field.properties) === null || _a === void 0 ? void 0 : _a.hasOwnProperty(property)) {
            return field.properties[property];
        }
        else if ((_b = field.dynamicProperties) === null || _b === void 0 ? void 0 : _b.hasOwnProperty(property)) {
            return field.dynamicProperties[property](this.formData);
        }
        else {
            return undefined;
        }
    };
    FormBuilderPartial.prototype.testId = function (field) {
        return field.testId;
    };
    FormBuilderPartial.prototype.slot = function (field) {
        return field.slot;
    };
    /**
     * For each emit handler defined in the field configuration,
     * it decorates the handler function with the event parameter and
     * binds the handler function to the emitSetField which is responsible
     * to trigger the state change
     */
    FormBuilderPartial.prototype.getEmitHandlers = function (field) {
        var _this = this;
        if (!field.emitHandlers) {
            return {};
        }
        return Object.entries(field.emitHandlers).reduce(function (acc, _a) {
            var _b;
            var eventName = _a[0], eventHandler = _a[1];
            return (__assign(__assign({}, acc), (_b = {}, _b[eventName] = function (event) { return __awaiter(_this, void 0, void 0, function () {
                var result, _a;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            _b.trys.push([0, 2, , 3]);
                            return [4 /*yield*/, eventHandler(event)];
                        case 1:
                            result = _b.sent();
                            if (result) {
                                this.emitSetField(result);
                            }
                            this.syncItemProperties();
                            return [3 /*break*/, 3];
                        case 2:
                            _a = _b.sent();
                            return [2 /*return*/];
                        case 3: return [2 /*return*/];
                    }
                });
            }); }, _b)));
        }, {});
    };
    FormBuilderPartial.prototype.emitSetField = function (value) {
        return value;
    };
    FormBuilderPartial.prototype.onComponentsChange = function () {
        this.initDebouncedValidators();
        this.validateComponents();
        this.syncItemProperties();
    };
    FormBuilderPartial.prototype.onFormDataChange = function () {
        this.syncItemProperties();
    };
    /**
     * Force the validation of the components initial state
     * Used in form submission where we want to show all errors (if any),
     * even for fields that have not been touched yet (thus, they wouldn't have been validated).
     */
    FormBuilderPartial.prototype.validateComponents = function () {
        var _this = this;
        this.flattenedComponents
            .filter(function (field) { return _this.shouldValidateField(field); })
            .forEach(function (field) {
            var value = _this.formData[field.id].value;
            var _a = _this.invokeValidators(field, value), isValid = _a.isValid, error = _a.error, isValidationPending = _a.isValidationPending;
            _this.emitStateUpdate({
                field: field,
                state: { isValid: isValid, error: error, isValidationPending: isValidationPending },
            });
        });
    };
    FormBuilderPartial.prototype.shouldValidateField = function (field) {
        // only data fields make sense to be validated
        // standalone fields don't use validators in the form config,
        // instead they provide their own validations
        return field.isDataField && !field.isStandAlone;
    };
    /**
     * Assign properties to components state each time the function gets executed
     * Used to update both static, dynamic and preloaded data
     * Called after: form change, emit handler call
     */
    FormBuilderPartial.prototype.syncItemProperties = function () {
        this.$nextTick(function () {
            var _this = this;
            this.flattenedComponents.forEach(function (item) {
                var _a;
                var itemRefs = _this.$refs[item.id];
                if (!itemRefs || itemRefs.length === 0) {
                    return;
                }
                var preloadedItemData = (_a = _this.preloadedData[item.id]) !== null && _a !== void 0 ? _a : {};
                var itemData = __assign(__assign({}, preloadedItemData), _this.getItemProperties(item));
                if (itemData.hasOwnProperty('value')) {
                    delete itemData.value;
                }
                for (var _i = 0, _b = Object.keys(itemData); _i < _b.length; _i++) {
                    var prop = _b[_i];
                    itemRefs[0][prop] = itemData[prop];
                }
            });
        });
    };
    Object.defineProperty(FormBuilderPartial.prototype, "flattenedComponents", {
        /**
         * Traverse the form configuration and store each `component` in a list
         * Used to filter out certain components in the list (e.g. data components)
         */
        get: function () {
            var _a;
            var allComponents = __spreadArray([], this.components);
            for (var _i = 0, allComponents_1 = allComponents; _i < allComponents_1.length; _i++) {
                var item = allComponents_1[_i];
                allComponents.push.apply(allComponents, ((_a = item.children) !== null && _a !== void 0 ? _a : []));
            }
            return allComponents;
        },
        enumerable: false,
        configurable: true
    });
    FormBuilderPartial.prototype.initDebouncedValidators = function () {
        var _this = this;
        if (Object.keys(this.debouncedValidators).length) {
            return;
        }
        var _loop_1 = function (field) {
            var _loop_2 = function (validator) {
                var key = this_1.getDebouncedValidatorKey(field, validator);
                this_1.debouncedValidators[key] = debounce(function (value) {
                    _this.invokeAsyncValidator(field, validator, value);
                }, AsyncDebounceInMs);
            };
            for (var _b = 0, _c = this_1.getAsyncValidators(field); _b < _c.length; _b++) {
                var validator = _c[_b];
                _loop_2(validator);
            }
        };
        var this_1 = this;
        for (var _i = 0, _a = this.components; _i < _a.length; _i++) {
            var field = _a[_i];
            _loop_1(field);
        }
    };
    FormBuilderPartial.prototype.emitStateUpdate = function (payload) {
        return payload;
    };
    /**
     * Emitted when a field inside form builder emits a value-update event
     * This event contains the value and the validity state of the field
     * @param payload
     * @returns {IValueUpdateModel}
     */
    FormBuilderPartial.prototype.emitValue = function (payload) {
        return payload;
    };
    /**
     * Emitted when a field inside form builder emits a field-touched event
     * This event is used to trigger validation on blur for certain fields
     * @param event
     * @param field
     * @returns {fieldId: string}
     */
    FormBuilderPartial.prototype.onFieldBlur = function (_, field) {
        if (!field.isDataField) {
            return { fieldId: field.id };
        }
        if (this.shouldValidateOnBlur(field)) {
            var fieldValue = this.formData[field.id].value;
            var _a = this.invokeValidators(field, fieldValue), isValid = _a.isValid, error = _a.error, isValidationPending = _a.isValidationPending;
            return { fieldId: field.id, isValid: isValid, error: error, isValidationPending: isValidationPending };
        }
    };
    FormBuilderPartial.prototype.onValueChange = function (event, field) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var value, _b, isValid, error, isValidationPending;
            return __generator(this, function (_c) {
                if (!((_a = event.detail) === null || _a === void 0 ? void 0 : _a.length)) {
                    return [2 /*return*/];
                }
                if (field.isStandAlone === true) {
                    return [2 /*return*/]; // in this case we rely on onStandAloneValueChange
                }
                value = event.detail[0];
                _b = this.invokeValidators(field, value), isValid = _b.isValid, error = _b.error, isValidationPending = _b.isValidationPending;
                this.emitValue({
                    field: field,
                    value: value,
                    isValid: isValid,
                    error: error,
                    isValidationPending: isValidationPending,
                    touched: true,
                });
                this.invokeTriggeredValidators(field, value);
                return [2 /*return*/];
            });
        });
    };
    FormBuilderPartial.prototype.onStandAloneValueChange = function (event, field) {
        return __awaiter(this, void 0, void 0, function () {
            var payload, isValid, value, error, touched, emittedValue;
            return __generator(this, function (_a) {
                payload = event.detail[0];
                isValid = payload.isValid, value = payload.value, error = payload.error, touched = payload.touched;
                emittedValue = value;
                if (field.valuePreProcess) {
                    emittedValue = field.valuePreProcess.apply(this, [
                        value,
                        this.formData,
                    ]);
                }
                // allow standalone components to override "touched" property
                // mainly for dynamic initialisation inside the component without user interaction
                // default to "true" if not specified, meaning a user interaction triggered it
                this.emitValue({
                    field: field,
                    value: emittedValue,
                    isValid: isValid,
                    error: error,
                    touched: touched !== null && touched !== void 0 ? touched : true,
                    isValidationPending: false,
                });
                this.invokeTriggeredValidators(field, value);
                return [2 /*return*/];
            });
        });
    };
    FormBuilderPartial.prototype.shouldShowValidationMessages = function (field) {
        return (field.validationDisplay === ValidationDisplay.OnTouchShowMessage ||
            field.validationDisplay === ValidationDisplay.OnTypeShowMessage);
    };
    FormBuilderPartial.prototype.shouldValidateOnBlur = function (field) {
        return !(field.validationDisplay === ValidationDisplay.OnTypeShowMessage ||
            field.validationDisplay === ValidationDisplay.OnTypeHighlight);
    };
    FormBuilderPartial.prototype.invokeValidators = function (field, value) {
        var _a = this.invokeSyncValidators(field, value), isValid = _a.isValid, error = _a.error;
        if (isValid) {
            this.invokeAsyncValidators(field, value);
            return {
                isValid: isValid,
                error: error,
                isValidationPending: this.getAsyncValidators(field).length > 0,
            };
        }
        return { isValid: isValid, error: error, isValidationPending: false };
    };
    /**
     * If the field has async validators, they get invoked.
     * In case they exist, a validation pending event is emitted when the validation begins
     * and a value change event is triggered when the async validation finishes.
     *
     * @param value field value
     * @param field field to be validated
     */
    FormBuilderPartial.prototype.invokeAsyncValidators = function (field, value) {
        var asyncValidators = this.getAsyncValidators(field);
        if (!asyncValidators.length) {
            return;
        }
        for (var _i = 0, asyncValidators_1 = asyncValidators; _i < asyncValidators_1.length; _i++) {
            var validator = asyncValidators_1[_i];
            var key = this.getDebouncedValidatorKey(field, validator);
            this.debouncedValidators[key](value);
        }
    };
    FormBuilderPartial.prototype.invokeAsyncValidator = function (field, validator, value) {
        return __awaiter(this, void 0, void 0, function () {
            var fieldId, isValid, error, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        fieldId = field.id;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, validator.fn(value, this.formData)];
                    case 2:
                        isValid = _b.sent();
                        if (this.isValidatorResultObsolete(fieldId, value)) {
                            this.emitValidationPending(field, false);
                            return [2 /*return*/];
                        }
                        error = !isValid ? validator.message : undefined;
                        this.emitStateUpdate({
                            field: field,
                            state: {
                                isValid: isValid,
                                error: error,
                                isValidationPending: false,
                            },
                        });
                        return [3 /*break*/, 4];
                    case 3:
                        _a = _b.sent();
                        this.emitValidationPending(field, false);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    FormBuilderPartial.prototype.getDebouncedValidatorKey = function (field, validator) {
        return field.id + "-" + validator.name;
    };
    FormBuilderPartial.prototype.isValidatorResultObsolete = function (fieldId, value) {
        // if the value of the field has changed while the validation was pending
        // then this validation result is no longer relevant since it refers to the old value
        return this.formData[fieldId].value !== value;
    };
    FormBuilderPartial.prototype.emitValidationPending = function (field, pending) {
        this.emitStateUpdate({
            field: field,
            state: { isValidationPending: pending },
        });
    };
    /**
     * Invoke only the synchronous validators for a specific field.
     * This will happen at any field value change event.
     *
     * @param field the field changed
     * @param value the value of the field
     * @returns object containing validity and error message (if any)
     */
    FormBuilderPartial.prototype.invokeSyncValidators = function (field, value) {
        return this.getValidationResult(field, value, this.formData);
    };
    /**
     * Given a field value has changed, invoke triggerd validators of other fields
     * And update their validation result in the store
     * This will happen at any field value change event.
     *
     * @param field the field changed
     * @param value the value of the field
     */
    FormBuilderPartial.prototype.invokeTriggeredValidators = function (field, value) {
        var _this = this;
        // form data with updated value for the given field
        // used to validate related fields against this value that hasn't been updated to the store yet
        var updatedFormData = this.getFormDataWithFieldValue(field, value);
        this.getFieldsToRevalidateFrom(field.id).forEach(function (triggeredField) {
            var triggeredFieldValue = _this.formData[triggeredField.id].value;
            var _a = _this.getValidationResult(triggeredField, triggeredFieldValue, updatedFormData), isValid = _a.isValid, error = _a.error;
            _this.emitStateUpdate({
                field: triggeredField,
                state: { isValid: isValid, error: error, isValidationPending: false },
            });
        });
    };
    FormBuilderPartial.prototype.getFormDataWithFieldValue = function (field, value) {
        var _a;
        return __assign(__assign({}, this.formData), (_a = {}, _a[field.id] = __assign(__assign({}, this.formData[field.id]), { value: value }), _a));
    };
    FormBuilderPartial.prototype.getValidationResult = function (field, value, formData) {
        var _a;
        var failingValidator = ((_a = field.validation) !== null && _a !== void 0 ? _a : []).find(function (validator) { return !validator.isAsync && !validator.fn(value, formData); });
        return failingValidator
            ? { isValid: false, error: failingValidator.message }
            : { isValid: true };
    };
    FormBuilderPartial.prototype.getFieldsToRevalidateFrom = function (field) {
        return this.components.filter(function (component) {
            var _a;
            return ((_a = component.validation) !== null && _a !== void 0 ? _a : []).some(function (validator) { var _a; return ((_a = validator.triggeringFields) !== null && _a !== void 0 ? _a : []).includes(field); });
        });
    };
    FormBuilderPartial.prototype.getAsyncValidators = function (field) {
        var _a;
        return ((_a = field.validation) !== null && _a !== void 0 ? _a : []).filter(function (validator) { return validator.isAsync; });
    };
    __decorate([
        Prop(),
        __metadata("design:type", Array)
    ], FormBuilderPartial.prototype, "components", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], FormBuilderPartial.prototype, "formData", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], FormBuilderPartial.prototype, "preloadedData", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Boolean)
    ], FormBuilderPartial.prototype, "nextStepBlocked", void 0);
    __decorate([
        Emit('set-field'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], FormBuilderPartial.prototype, "emitSetField", null);
    __decorate([
        Watch('components', { immediate: true, deep: true }),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], FormBuilderPartial.prototype, "onComponentsChange", null);
    __decorate([
        Watch('formData', { deep: true }),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], FormBuilderPartial.prototype, "onFormDataChange", null);
    __decorate([
        Emit('state-update'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], FormBuilderPartial.prototype, "emitStateUpdate", null);
    __decorate([
        Emit('value-update'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], FormBuilderPartial.prototype, "emitValue", null);
    __decorate([
        Emit('field-touched'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [CustomEvent, Object]),
        __metadata("design:returntype", void 0)
    ], FormBuilderPartial.prototype, "onFieldBlur", null);
    FormBuilderPartial = __decorate([
        Component({
            name: 'form-builder-partial',
            inheritAttrs: false,
        })
    ], FormBuilderPartial);
    return FormBuilderPartial;
}(Mixins(MagicConfigMixin)));

export default FormBuilderPartial;
